import { DialogComponent } from '@theme/dialog';

const SUBSCRIBED_KEY = 'zachlawi:insiderSubscribed';
const SEEN_SESSION_KEY = 'zachlawi:insiderSeenThisSession';
const AGE_VERIFIED_KEY = 'ageVerified';

/**
 * Zachlawi Insiders promo popup.
 *
 * Sequencing rules:
 *  - Home page only (enforced in Liquid — the element only renders there).
 *  - Never before the age gate is cleared. The age gate (sections/age-gate.liquid)
 *    writes localStorage "ageVerified" and hides itself, but dispatches no event,
 *    so we hook its Yes button and also poll as a safety net.
 *  - Suppressed permanently once the visitor has subscribed from this browser.
 *  - Frequency is a theme setting: "session" (once per browser session) or
 *    "always" (every home page view).
 *
 * @extends {DialogComponent}
 */
class InsiderPopup extends DialogComponent {
  #pollTimer = null;
  #openTimer = null;

  connectedCallback() {
    super.connectedCallback();

    // Any link pointing at #zachlawi-insiders (the announcement bar) opens this
    // on demand, from any page, ignoring the frequency rules — an explicit click
    // is a stronger signal than the automatic timing.
    document.addEventListener('click', this.#handleTriggerClick, true);

    // Returning from a successful submit: show the confirmation immediately,
    // and never show the signup form again on this browser.
    if (this.querySelector('[data-posted="true"]')) {
      this.#remember(SUBSCRIBED_KEY);
      this.showDialog();
      return;
    }

    // Off the home page the popup only ever opens via the banner link.
    if (!this.#autoOpen) return;
    if (!this.#shouldShow()) return;

    if (this.#ageVerified) {
      this.#scheduleOpen();
    } else {
      this.#waitForAgeGate();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleTriggerClick, true);
    clearTimeout(this.#openTimer);
    clearInterval(this.#pollTimer);
  }

  get #autoOpen() {
    return this.dataset.autoOpen === 'true';
  }

  /**
   * @param {MouseEvent} event
   */
  #handleTriggerClick = (event) => {
    const clicked = event.target instanceof Element ? event.target : event.target?.parentElement;
    if (!clicked?.closest('a[href*="#zachlawi-insiders"]')) return;

    event.preventDefault();
    this.#remember(SEEN_SESSION_KEY);
    this.showDialog();
  };

  get #frequency() {
    return this.dataset.frequency === 'always' ? 'always' : 'session';
  }

  get #delay() {
    const seconds = Number(this.dataset.delay);
    return Number.isFinite(seconds) ? Math.max(0, seconds) * 1000 : 3000;
  }

  get #ageVerified() {
    return this.#read(AGE_VERIFIED_KEY, localStorage) === 'true';
  }

  /**
   * Storage can throw (private mode, blocked cookies) — never let that break the page.
   *
   * @param {string} key
   * @param {Storage} store
   * @returns {string | null}
   */
  #read(key, store) {
    try {
      return store.getItem(key);
    } catch {
      return null;
    }
  }

  /** @param {string} key */
  #remember(key) {
    try {
      if (key === SEEN_SESSION_KEY) sessionStorage.setItem(key, 'true');
      else localStorage.setItem(key, 'true');
    } catch {
      /* storage unavailable — popup simply repeats next load */
    }
  }

  #shouldShow() {
    if (this.#read(SUBSCRIBED_KEY, localStorage) === 'true') return false;
    if (this.#frequency === 'session' && this.#read(SEEN_SESSION_KEY, sessionStorage) === 'true') {
      return false;
    }
    return true;
  }

  /**
   * The age gate offers no event, so listen for its Yes button and poll as backup
   * (covers the gate being re-rendered, or verified in another tab).
   */
  #waitForAgeGate() {
    document.getElementById('age-yes')?.addEventListener('click', () => this.#scheduleOpen(), {
      once: true,
    });

    this.#pollTimer = setInterval(() => {
      if (!this.#ageVerified) return;
      clearInterval(this.#pollTimer);
      this.#scheduleOpen();
    }, 500);
  }

  #scheduleOpen() {
    clearInterval(this.#pollTimer);
    clearTimeout(this.#openTimer);

    this.#openTimer = setTimeout(() => {
      if (!this.#shouldShow()) return;
      this.#remember(SEEN_SESSION_KEY);
      this.showDialog();
    }, this.#delay);
  }
}

if (!customElements.get('insider-popup')) {
  customElements.define('insider-popup', InsiderPopup);
}
